/**
 * "Gather child pages" (`061-editor.md`): pull the bodies of a page's direct children into
 * the parent, so a parent page can serve as a combined read of its sub-pages.
 *
 * This is the PURE half — turning the fetched child pages into a ProseMirror node array —
 * kept free of React and Tiptap so the ordering and the separator logic can be unit-tested
 * without mounting an editor. The impure half (reading the tree, fetching bodies, replacing
 * the live document) lives in `PageView` and `Editor`.
 */
import type { PageDto, ProseMirrorNode } from '@/api/endpoints/pages';

/**
 * Build the combined body for `children`, in the order given (the caller passes them in
 * tree order). For each child: its title as a level-2 heading, then its content, with a
 * horizontal rule BETWEEN pages — not before the first, not after the last.
 *
 * A child with an empty body still contributes its heading followed by one empty paragraph,
 * so the result is always a valid ProseMirror document.
 */
export function buildGatheredContent(
  children: readonly PageDto[],
  untitledLabel = 'Untitled',
): ProseMirrorNode[] {
  const nodes: ProseMirrorNode[] = [];

  children.forEach((child, index) => {
    if (index > 0) nodes.push({ type: 'horizontalRule' });

    const title = child.title.trim() || untitledLabel;
    nodes.push({
      type: 'heading',
      attrs: { level: 2 },
      // A text node may never be empty in ProseMirror, so the fallback above matters.
      content: [{ type: 'text', text: title }],
    });

    const body = child.content.content ?? [];
    nodes.push(...(body.length > 0 ? body : [{ type: 'paragraph' }]));
  });

  return nodes;
}
